import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Paper, Typography
} from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { authGet } from '../../services/apiService';
import { absoluteHttpUrl } from './assigneeDisplay';
import { formatSfTableDate } from './sfTableFormatting';
import { DEEP_GREEN, INK, MUTED, NEUTRAL_SURFACE } from './colorTokens';

const PANEL_SX = { p: 2, border: '1px solid rgba(26,46,34,0.1)', borderRadius: 1.5 };

const SnapshotRow = ({ label, children }) => {
  if (children == null || children === '' || children === false) return null;
  return (
    <>
      <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>{label}</Typography>
      <Typography component="div" sx={{ fontWeight: 600, fontSize: '0.8rem', color: INK, wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </>
  );
};

const dateOrNull = (value) => (value ? formatSfTableDate(value) : null);

/** L2 / L3 ADO work item details, opened from a Ticket Journey box. Stored data shows at once; the discussion loads from ADO. */
const AdoItemDetailsDialog = ({ workItemId, fallbackUrl, color, onClose }) => {
  const [detail, setDetail] = useState({ loading: true, item: null, related: [], error: '' });
  const [discussion, setDiscussion] = useState({ loading: true, comments: [], error: '' });

  useEffect(() => {
    let cancelled = false;
    setDetail({ loading: true, item: null, related: [], error: '' });
    setDiscussion({ loading: true, comments: [], error: '' });
    authGet(`/api/customer-issues/ado-details/${encodeURIComponent(workItemId)}`)
      .then((res) => {
        if (cancelled) return;
        if (res && res.success && res.item) setDetail({ loading: false, item: res.item, related: res.related || [], error: '' });
        else setDetail({ loading: false, item: null, related: [], error: (res && res.message) || 'Work item not found.' });
      })
      .catch((err) => {
        if (!cancelled) setDetail({ loading: false, item: null, related: [], error: (err && err.message) || 'Failed to load work item.' });
      });
    authGet(`/api/customer-issues/ado-comments/${encodeURIComponent(workItemId)}`)
      .then((res) => {
        if (!cancelled) setDiscussion({ loading: false, comments: (res && res.comments) || [], error: '' });
      })
      .catch((err) => {
        if (!cancelled) setDiscussion({ loading: false, comments: [], error: (err && err.message) || 'Failed to load discussion.' });
      });
    return () => { cancelled = true; };
  }, [workItemId]);

  const { item } = detail;
  const url = absoluteHttpUrl((item && item.url) || fallbackUrl);

  return (
    <Dialog open onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 700 }}>
        <span>
          {item && item.workItemType ? `${item.workItemType} · ` : 'ADO work item · '}
          <Box component="span" sx={{ color }}>{workItemId}</Box>
        </span>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, color: MUTED }}>Close</Button>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: NEUTRAL_SURFACE }}>
        {detail.loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>}
        {detail.error && <Alert severity="error">{detail.error}</Alert>}
        {item && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <Paper elevation={0} sx={PANEL_SX}>
                <Typography sx={{ fontWeight: 700, color: DEEP_GREEN, fontSize: '0.95rem', mb: 0.5 }}>{item.title}</Typography>
                <Divider sx={{ my: 1.25 }} />
                <Typography sx={{ color: MUTED, fontSize: '0.75rem', mb: 0.5 }}>Description</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: INK, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {item.description || 'No description.'}
                </Typography>
              </Paper>
              <Paper elevation={0} sx={PANEL_SX}>
                <Typography sx={{ fontWeight: 700, color: DEEP_GREEN, fontSize: '0.95rem', mb: 1.25 }}>Discussion</Typography>
                {discussion.loading && <CircularProgress size={20} />}
                {discussion.error && <Alert severity="warning">{discussion.error}</Alert>}
                {!discussion.loading && !discussion.error && discussion.comments.length === 0 && (
                  <Typography sx={{ color: MUTED, fontSize: '0.85rem' }}>No discussion comments on this work item.</Typography>
                )}
                {discussion.comments.map((comment) => (
                  <Box key={comment.id} sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: INK }}>
                      {comment.author || 'Unknown'}
                      <Typography component="span" sx={{ fontSize: '0.7rem', color: MUTED, ml: 1, fontWeight: 400 }}>
                        {comment.date ? new Date(comment.date).toLocaleString() : ''}
                      </Typography>
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: INK, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {comment.text}
                    </Typography>
                    <Divider sx={{ mt: 1.25 }} />
                  </Box>
                ))}
              </Paper>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <Paper elevation={0} sx={PANEL_SX}>
                <Typography sx={{ fontWeight: 700, color: DEEP_GREEN, fontSize: '0.95rem', mb: 1.25 }}>Ticket snapshot</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 12px', alignItems: 'start' }}>
                  <SnapshotRow label="State">{item.state}</SnapshotRow>
                  <SnapshotRow label="Type">{item.workItemType}</SnapshotRow>
                  <SnapshotRow label="Priority">{item.priority}</SnapshotRow>
                  <SnapshotRow label="Severity">{item.severity}</SnapshotRow>
                  <SnapshotRow label="Assigned to">{item.assignedTo}</SnapshotRow>
                  <SnapshotRow label="Team">{item.team}</SnapshotRow>
                  <SnapshotRow label="Area">{item.areaPath}</SnapshotRow>
                  <SnapshotRow label="Iteration">{item.iterationPath}</SnapshotRow>
                  <SnapshotRow label="Fix version">{item.fixVersion}</SnapshotRow>
                  <SnapshotRow label="Affects version">{item.affectVersion}</SnapshotRow>
                  <SnapshotRow label="Component">{item.component}</SnapshotRow>
                  <SnapshotRow label="Sub-component">{item.subComponent}</SnapshotRow>
                  <SnapshotRow label="Environment">{item.environment}</SnapshotRow>
                  <SnapshotRow label="Customer">{item.customer}</SnapshotRow>
                  <SnapshotRow label="Tags">{(item.tags || []).length ? item.tags.join(', ') : null}</SnapshotRow>
                  <SnapshotRow label="Created">{dateOrNull(item.createdDate)}</SnapshotRow>
                  <SnapshotRow label="Last changed">{dateOrNull(item.changedDate)}</SnapshotRow>
                  <SnapshotRow label="Closed">{dateOrNull(item.closedDate)}</SnapshotRow>
                </Box>
                {url && (
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mt: 2, textTransform: 'none', borderColor: 'rgba(26,46,34,0.25)', color: DEEP_GREEN, fontWeight: 600 }}
                  >
                    Open in Azure DevOps
                  </Button>
                )}
              </Paper>

              <Paper elevation={0} sx={PANEL_SX}>
                <Typography sx={{ fontWeight: 700, color: DEEP_GREEN, fontSize: '0.95rem', mb: 1.25 }}>Parent and related items</Typography>
                {detail.related.length === 0 ? (
                  <Typography sx={{ color: MUTED, fontSize: '0.85rem' }}>No parent or related items stored.</Typography>
                ) : detail.related.map((row) => (
                  <Box key={row.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                    <Chip size="small" label={row.relation} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color }}>{row.id}</Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: INK }}>{row.title}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: MUTED }}>{[row.type, row.state].filter(Boolean).join(' · ')}</Typography>
                  </Box>
                ))}
              </Paper>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdoItemDetailsDialog;
